import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { isAfter, addMinutes } from 'date-fns';
import memoize from 'memoize-one';

import type { ClientBasedAuth } from '@atlaskit/media-core';

import type {
	ContentMediaSessionQuery_contentMediaSession,
	ContentMediaSessionQuery as ContentMediaSessionQueryType,
	ContentMediaSessionQueryVariables,
} from './__types__/ContentMediaSessionQuery';
import { ContentMediaSessionQuery } from './ContentMediaSession.graphql';

type ContentMediaSessionQueryStrict = {
	contentMediaSession: ContentMediaSessionQuery_contentMediaSession;
};

const isMediaSessionQueryDataValid = (
	data?: ContentMediaSessionQueryType | null,
): data is ContentMediaSessionQueryStrict => Boolean(data?.contentMediaSession);

const createMediaSessionConfig = memoize(
	(data: ContentMediaSessionQueryStrict): ClientBasedAuth => ({
		clientId: data.contentMediaSession.configuration.clientId,
		baseUrl: data.contentMediaSession.configuration.fileStoreUrl,
		token: data.contentMediaSession.token.value,
	}),
);

type UseAuthProviderProps = {
	contentId: string | undefined;
	// take into account that authProvider function will be deffered to the media client
	// and will be invoked when user starts uploading a file
	// onMediaSessionError might be triggered on that moment if there is an error with getting the token
	onMediaSessionError?: (err: Error) => void;
};

export const useAuthProvider = ({ contentId, onMediaSessionError }: UseAuthProviderProps) => {
	const client = useApolloClient();

	const getMediaSessionFromCache = useCallback(() => {
		try {
			if (!contentId) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' contentId is not provided.",
				);
			}
			const cachedData = client.readQuery<
				ContentMediaSessionQueryType,
				ContentMediaSessionQueryVariables
			>({
				query: ContentMediaSessionQuery,
				variables: { contentId },
			});
			if (!isMediaSessionQueryDataValid(cachedData)) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' invalid or missing data received from cache.",
				);
			}
			return cachedData;
		} catch {
			// expected behavior
			// "readQuery" throws if there is no data in the store
			return;
		}
	}, [contentId, client]);

	const getMediaSessionFromServer = useCallback(async () => {
		try {
			if (!contentId) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' contentId is not provided.",
				);
			}
			const response = await client.query<
				ContentMediaSessionQueryType,
				ContentMediaSessionQueryVariables
			>({
				query: ContentMediaSessionQuery,
				variables: { contentId },
				fetchPolicy: 'network-only',
			});
			if (!isMediaSessionQueryDataValid(response.data)) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' invalid or missing data received from server.",
				);
			}
			if (response?.errors?.[0]) {
				throw new Error(response.errors[0].message);
			}
			return response.data;
		} catch (err) {
			if (onMediaSessionError) {
				onMediaSessionError(err);
			}
			throw err;
		}
	}, [client, contentId, onMediaSessionError]);

	// Builds the media session config, including the token, from cache or server
	// It doesn't retry.
	const authProvider = useCallback(async () => {
		// countentId should be defined, otherwise this function shouldn't be called
		const cachedMediaSession = getMediaSessionFromCache();

		if (cachedMediaSession) {
			const expirationDateTime = new Date(
				cachedMediaSession.contentMediaSession.token.expiryDateTime,
			);
			// It's expected to expire in 10mins. We'll renew it after 8mins.
			if (!isAfter(new Date(), addMinutes(expirationDateTime, -2))) {
				return createMediaSessionConfig(cachedMediaSession);
			}
		}

		const freshMediaSession = await getMediaSessionFromServer();
		return createMediaSessionConfig(freshMediaSession);
	}, [getMediaSessionFromCache, getMediaSessionFromServer]);

	if (!contentId) {
		return null;
	}

	return authProvider;
};
